import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, FileText, Users, Store, CreditCard, Scale, Shield, Gavel, Mail } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import { generateBreadcrumbSchema, SEO_CONFIG } from '../utils/seo';

const TermsOfServicePage = () => {
  const { t } = useTranslation();
  
  // Breadcrumb schema for search results
  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: '/' },
    { name: 'Terms of Service' }
  ]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="terms-page">
      <SEOHead 
        title="Terms of Service | TradeHub24"
        description="Read TradeHub24's terms of service. Understand your rights and responsibilities when using our virtual tradeshow platform."
        canonicalUrl={`${SEO_CONFIG.siteUrl}/terms-of-service`}
        structuredData={breadcrumbSchema}
      />
      
      {/* Hero Section */}
      <section className="bg-primary text-white py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center text-white/80 hover:text-white mb-4 transition-colors text-sm">
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('legal.back_to_home')}
          </Link>
          <div className="flex items-center gap-3">
            <FileText className="h-10 w-10" />
            <h1 className="text-3xl sm:text-4xl font-bold">{t('legal.terms.title')}</h1>
          </div>
          <p className="mt-4 text-blue-100">{t('legal.last_updated')}</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-8 space-y-8">
            
            {/* Introduction */}
            <div>
              <h2 className="text-xl font-bold text-slate-900 mb-4">1. {t('legal.terms.intro_title')}</h2>
              <p className="text-slate-600 leading-relaxed">
                {t('legal.terms.intro_text')}
              </p>
            </div>

            {/* User Accounts */}
            <div className="border-t border-slate-200 pt-8">
              <div className="flex items-center gap-2 mb-4">
                <Users className="h-5 w-5 text-primary" />
                <h2 className="text-xl font-bold text-slate-900">2. {t('legal.terms.accounts_title')}</h2>
              </div>
              <p className="text-slate-600 mb-4">{t('legal.terms.accounts_text')}</p>
              <ul className="list-disc list-inside space-y-2 text-slate-600">
                <li>{t('legal.terms.accounts_1')}</li>
                <li>{t('legal.terms.accounts_2')}</li>
                <li>{t('legal.terms.accounts_3')}</li>
                <li>{t('legal.terms.accounts_4')}</li>
                <li>{t('legal.terms.accounts_5')}</li>
              </ul>
            </div>

            {/* Exhibitor Obligations */}
            <div className="border-t border-slate-200 pt-8">
              <div className="flex items-center gap-2 mb-4">
                <Store className="h-5 w-5 text-primary" />
                <h2 className="text-xl font-bold text-slate-900">3. {t('legal.terms.exhibitor_title')}</h2>
              </div>
              <p className="text-slate-600 mb-4">{t('legal.terms.exhibitor_text')}</p>
              <ul className="list-disc list-inside space-y-2 text-slate-600">
                <li>{t('legal.terms.exhibitor_1')}</li>
                <li>{t('legal.terms.exhibitor_2')}</li>
                <li>{t('legal.terms.exhibitor_3')}</li>
                <li>{t('legal.terms.exhibitor_4')}</li>
                <li>{t('legal.terms.exhibitor_5')}</li>
              </ul>
            </div>

            {/* Attendee Conduct */}
            <div className="border-t border-slate-200 pt-8">
              <div className="flex items-center gap-2 mb-4">
                <Users className="h-5 w-5 text-primary" />
                <h2 className="text-xl font-bold text-slate-900">4. {t('legal.terms.attendee_title')}</h2>
              </div>
              <p className="text-slate-600 mb-4">{t('legal.terms.attendee_text')}</p>
              <ul className="list-disc list-inside space-y-2 text-slate-600">
                <li>{t('legal.terms.attendee_1')}</li>
                <li>{t('legal.terms.attendee_2')}</li>
                <li>{t('legal.terms.attendee_3')}</li>
                <li>{t('legal.terms.attendee_4')}</li>
              </ul>
            </div>

            {/* Payments & Refunds */}
            <div className="border-t border-slate-200 pt-8">
              <div className="flex items-center gap-2 mb-4">
                <CreditCard className="h-5 w-5 text-primary" />
                <h2 className="text-xl font-bold text-slate-900">5. {t('legal.terms.payments_title')}</h2>
              </div>
              <p className="text-slate-600 mb-4">{t('legal.terms.payments_text')}</p>
              <ul className="list-disc list-inside space-y-2 text-slate-600">
                <li>{t('legal.terms.payments_1')}</li>
                <li>{t('legal.terms.payments_2')}</li>
                <li>{t('legal.terms.payments_3')}</li>
                <li>{t('legal.terms.payments_4')}</li>
              </ul>
            </div>

            {/* Intellectual Property */}
            <div className="border-t border-slate-200 pt-8">
              <div className="flex items-center gap-2 mb-4">
                <Scale className="h-5 w-5 text-primary" />
                <h2 className="text-xl font-bold text-slate-900">6. {t('legal.terms.ip_title')}</h2>
              </div>
              <p className="text-slate-600 mb-4">{t('legal.terms.ip_text')}</p>
              <ul className="list-disc list-inside space-y-2 text-slate-600">
                <li>{t('legal.terms.ip_1')}</li>
                <li>{t('legal.terms.ip_2')}</li>
                <li>{t('legal.terms.ip_3')}</li>
                <li>{t('legal.terms.ip_4')}</li>
              </ul>
            </div>

            {/* Limitation of Liability */}
            <div className="border-t border-slate-200 pt-8">
              <div className="flex items-center gap-2 mb-4">
                <Shield className="h-5 w-5 text-primary" />
                <h2 className="text-xl font-bold text-slate-900">7. {t('legal.terms.liability_title')}</h2>
              </div>
              <p className="text-slate-600 mb-4">{t('legal.terms.liability_text')}</p>
              <ul className="list-disc list-inside space-y-2 text-slate-600">
                <li>{t('legal.terms.liability_1')}</li>
                <li>{t('legal.terms.liability_2')}</li>
                <li>{t('legal.terms.liability_3')}</li>
                <li>{t('legal.terms.liability_4')}</li>
              </ul>
            </div>

            {/* Termination */}
            <div className="border-t border-slate-200 pt-8">
              <h2 className="text-xl font-bold text-slate-900 mb-4">8. {t('legal.terms.termination_title')}</h2>
              <p className="text-slate-600 leading-relaxed">
                {t('legal.terms.termination_text')}
              </p>
            </div>

            {/* Governing Law */}
            <div className="border-t border-slate-200 pt-8">
              <div className="flex items-center gap-2 mb-4">
                <Gavel className="h-5 w-5 text-primary" />
                <h2 className="text-xl font-bold text-slate-900">9. {t('legal.terms.governing_title')}</h2>
              </div>
              <p className="text-slate-600 leading-relaxed">
                {t('legal.terms.governing_text')}
              </p>
            </div>

            {/* Contact Us */}
            <div className="border-t border-slate-200 pt-8">
              <div className="flex items-center gap-2 mb-4">
                <Mail className="h-5 w-5 text-primary" />
                <h2 className="text-xl font-bold text-slate-900">10. {t('legal.terms.contact_title')}</h2>
              </div>
              <p className="text-slate-600 leading-relaxed">
                {t('legal.terms.contact_text')}
              </p>
              <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                <p className="text-slate-700"><strong>Email:</strong> legal@tradehub24.com</p>
                <p className="text-slate-700 mt-2"><strong>Phone:</strong> +1 (980) 613-4193 (USA) | +33-(0) (Europe)</p>
                <p className="text-slate-700 mt-2"><strong>Address:</strong> 10926 David Taylor Drive, Suite 120, Charlotte, NC 28262 USA</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsOfServicePage;
